/**
 * CHAT-02b — chat data layer (read + mark-as-read + send).
 *
 * Backs the flag-gated chat surface (ConversationList / ConversationThread /
 * Composer). Everything here is dormant in prod behind `chat_cta_enabled`.
 *
 * Locked contract (do NOT re-derive — see the CHAT-02b item body):
 *  - CHAT-11 triggers maintain `last_message_at` + the unread counters. We READ
 *    those columns and NEVER write them directly.
 *  - The send path is the `send-message` Edge Function. We never INSERT into
 *    `messages` from the client.
 *  - Mark-as-read is a direct `messages.read_at` UPDATE (the recipient RLS
 *    policy allows it; the BEFORE UPDATE guard rejects touching any other
 *    column — so we only ever set read_at).
 *  - Token: `getValidAccessToken()` is the app's canonical, auto-refreshing
 *    session token (kept in sync with the Supabase session by AuthContext).
 *    `supabase.auth.getSession()` can be empty here because the app stores auth
 *    under custom localStorage keys (see lib/api/blogApi.ts), so we use the
 *    former to guarantee a fresh, valid bearer for the Edge Function.
 */

import { useSyncExternalStore } from 'react';
import { apiGet, apiRpc, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/api/client';
import { getValidAccessToken } from '../../lib/api/authApi';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface DbConversation {
  id: string;
  listing_id: string;
  agent_id: string;
  buyer_id: string;
  created_at: string;
  last_message_at: string;
  status: string;
  buyer_unread_count: number;
  agent_unread_count: number;
  first_response_at: string | null;
  response_time_hours: number | null;
}

export interface DbMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  sent_at: string;
  read_at: string | null;
  /**
   * Set when redact_message_content() masked PII into `content`. Surfaced so the
   * UI can flag a redacted message. The ORIGINAL un-redacted text lives in
   * `messages.raw_content`, which the counterparty path MUST NEVER read — see
   * MESSAGE_SAFE_COLUMNS below and BUG-032.
   */
  redacted_at?: string | null;
  /**
   * CHAT-08 — true for number-free system notices inserted by the
   * reveal_conversation_phone RPC (never by the client). The thread renders these
   * as a centered system notice, not a normal bubble.
   */
  is_system?: boolean;
}

/**
 * BUG-032 — the explicit, NON-admin safe column set for reading `messages`.
 *
 * `messages.raw_content` holds the ORIGINAL un-redacted PII that
 * redact_message_content() masks out of `content`. A counterparty's client must
 * never receive it, so every non-admin read MUST list these columns explicitly
 * and MUST NOT use `select=*` (which would pull `raw_content` and
 * `notification_sent_at`). The DB-level `REVOKE SELECT (raw_content)` is the hard
 * guarantee; this list is the client-side belt-and-braces that also lets the
 * REVOKE be applied safely (a lingering `select=*` would break message loading).
 */
const MESSAGE_SAFE_COLUMNS = 'id,conversation_id,sender_id,content,sent_at,read_at,redacted_at,is_system';

/** A conversation enriched for the list/thread UI from the viewer's perspective. */
export interface ConversationListItem {
  conversation: DbConversation;
  /** True when the current viewer is the buyer in this conversation. */
  viewerIsBuyer: boolean;
  counterpartyName: string;
  counterpartyAvatarUrl: string | null;
  listingTitle: string;
  /** Unread count for the viewer: buyer_unread_count or agent_unread_count. */
  unreadCount: number;
}

/**
 * Flat result shape (not a discriminated union): this project compiles with
 * strictNullChecks OFF, where `if (result.ok)` does NOT narrow a discriminated
 * union, so every consumer would otherwise see spurious "property does not
 * exist" errors. Optional fields keep all branches accessible without narrowing.
 */
export interface SendMessageResult {
  ok: boolean;
  id?: string;
  /** HTTP status on failure (0 for a network error). */
  status?: number;
  error?: string;
  message?: string;
  upgrade_url?: string;
  retry_after?: number;
}

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

/**
 * Counterparty identity as returned by the participant-gated RPC.
 *
 * CHAT-08 widened the RPC to 4 columns: it now also returns the counterparty's
 * phone (null UNLESS this conversation's number reveal has happened) and the
 * conversation's `phone_revealed_at` timestamp (null = not revealed). The list
 * enrichment only reads name/avatar; the thread header reads all four to drive
 * the trust-gated call button.
 */
export interface CounterpartyDetails {
  display_name: string;
  avatar_url: string | null;
  counterparty_phone: string | null;
  phone_revealed_at: string | null;
}

/**
 * Resolve a conversation's counterparty (the other participant) via the
 * participant-gated `get_conversation_counterparty` RPC.
 *
 * CHAT-15: profiles RLS no longer lets an agent read a buyer's row (TECH-014),
 * so we can't read `profiles` directly for the counterparty. The RPC is a
 * SECURITY DEFINER function that returns ONLY display_name + avatar_url, and
 * only to a verified participant — so it works for BOTH directions (buyer→agent
 * and agent→buyer) without ever exposing email/phone. Returns null on any
 * failure (non-participant, network) so the caller can fall back to a neutral
 * role label.
 */
async function fetchCounterparty(conversationId: string, token: string): Promise<CounterpartyDetails | null> {
  const { data } = await apiRpc<CounterpartyDetails[]>(
    'get_conversation_counterparty',
    { p_conversation_id: conversationId },
    token
  );
  return data && data.length > 0 ? data[0] : null;
}

/**
 * CHAT-08 — thread-level counterparty fetch (acquires its own token).
 *
 * The conversation list enriches via the private `fetchCounterparty` above, but
 * the open thread needs the FULL 4-column shape (incl. counterparty_phone +
 * phone_revealed_at) and must be able to re-fetch after a reveal so the header
 * flips without a remount. Returns null on any failure so the header degrades to
 * the not-revealed state.
 */
export async function fetchConversationCounterparty(
  conversationId: string
): Promise<CounterpartyDetails | null> {
  const token = await getValidAccessToken();
  if (!token) return null;
  return fetchCounterparty(conversationId, token);
}

/** Result of a phone-reveal attempt. Flat shape (see SendMessageResult). */
export interface RevealPhoneResult {
  ok: boolean;
  /** The conversation's phone_revealed_at after the call (ISO string), when ok. */
  revealedAt?: string | null;
  /** Raw RPC error (e.g. 'cannot reveal: suspended or blocked') — for logs only. */
  error?: string;
}

/**
 * CHAT-08 — reveal both participants' numbers for a conversation.
 *
 * Calls the SECURITY DEFINER `reveal_conversation_phone` RPC, which is
 * participant + can_send_message (suspension/block) gated and idempotent: it sets
 * `phone_revealed_at` once and inserts the number-free is_system notice. We NEVER
 * insert that message from the client, and the counterparty push fires from the
 * existing message-insert trigger — so this wrapper only forwards the call and
 * returns the resulting timestamp. On any RPC error we return ok:false with the
 * raw message kept OFF the happy path (the caller shows a calm, generic notice).
 */
export async function revealConversationPhone(conversationId: string): Promise<RevealPhoneResult> {
  const token = await getValidAccessToken();
  if (!token) return { ok: false, error: 'not authenticated' };

  const { data, error } = await apiRpc<string | null>(
    'reveal_conversation_phone',
    { p_conversation_id: conversationId },
    token
  );
  if (error) return { ok: false, error: error.message };
  return { ok: true, revealedAt: (data as string | null) ?? null };
}

/**
 * CHAT-19 — buyer shares their OWN number with the agent for this conversation.
 *
 * Mirrors `revealConversationPhone` (CHAT-08), but the reveal is buyer-initiated
 * and driven by a specific message the buyer just sent containing a number. Calls
 * the SECURITY DEFINER `reveal_buyer_phone` RPC, which is buyer-only + participant
 * gated and idempotent: it reads the buyer's number from that message's
 * `raw_content`, sets `conversations.buyer_phone_revealed_at` +
 * `buyer_phone_message_id`, and inserts the number-free is_system notice. We NEVER
 * insert that message from the client, and the agent's call button renders via the
 * existing viewer-aware counterparty path with no extra client code — so this
 * wrapper only forwards the call and returns the resulting timestamp. On any RPC
 * error we return ok:false with the raw message kept OFF the happy path (the
 * caller shows a calm, generic notice).
 */
export async function revealBuyerPhone(
  conversationId: string,
  messageId: string
): Promise<RevealPhoneResult> {
  const token = await getValidAccessToken();
  if (!token) return { ok: false, error: 'not authenticated' };

  const { data, error } = await apiRpc<string | null>(
    'reveal_buyer_phone',
    { p_conversation_id: conversationId, p_message_id: messageId },
    token
  );
  if (error) return { ok: false, error: error.message };
  return { ok: true, revealedAt: (data as string | null) ?? null };
}

/**
 * Fetch the viewer's conversations (RLS returns only theirs), newest activity
 * first, enriched with the counterparty's name/avatar and the listing title.
 *
 * The counterparty name/avatar comes from the participant-gated RPC (see
 * fetchCounterparty) rather than a direct profiles read, which RLS no longer
 * permits for buyer rows. The RPC already returns a role-aware fallback name
 * ('Agent'/'Buyer'), so the local fallback only applies if the RPC itself fails.
 * Listings are public when published, so the title resolves for both sides.
 */
export async function fetchConversations(userId: string): Promise<ConversationListItem[]> {
  const token = await getValidAccessToken();
  if (!token) {
    // No session: this viewer has no conversations — publish an empty cache so
    // the nav badge reads 0 rather than a stale prior-user count.
    setConversationsCache([], userId);
    return [];
  }

  const { data: conversations, error } = await apiGet<DbConversation[]>(
    'conversations',
    'select=*&order=last_message_at.desc',
    token
  );
  // On a network/RLS error, leave the existing cache untouched (don't clobber a
  // good count with a transient failure); the caller still gets [] as before.
  if (error) return [];
  if (!conversations || conversations.length === 0) {
    setConversationsCache([], userId);
    return [];
  }

  const listingIds = [...new Set(conversations.map((c) => c.listing_id))];

  const [listingsRes, counterparties] = await Promise.all([
    apiGet<Array<{ id: string; title: string | null; address: string | null }>>(
      'listings',
      `id=in.(${listingIds.join(',')})&select=id,title,address`,
      token
    ),
    Promise.all(conversations.map((c) => fetchCounterparty(c.id, token))),
  ]);

  const listingMap = new Map((listingsRes.data || []).map((l) => [l.id, l]));
  const counterpartyMap = new Map(conversations.map((c, i) => [c.id, counterparties[i]]));

  const items = conversations.map((c) =>
    enrich(c, userId, listingMap, counterpartyMap.get(c.id) || null)
  );
  // Single source of truth: every fetch (initial load, post-send, and the
  // post-read refetch driven by markConversationRead → onConversationsChanged)
  // publishes here, so the nav unread badge and the ConversationList row badges
  // always derive from the same data and stay in sync without a remount.
  setConversationsCache(items, userId);
  return items;
}

function enrich(
  c: DbConversation,
  userId: string,
  listingMap: Map<string, { title: string | null; address: string | null }>,
  counterparty: CounterpartyDetails | null
): ConversationListItem {
  const viewerIsBuyer = c.buyer_id === userId;
  const listing = listingMap.get(c.listing_id);
  return {
    conversation: c,
    viewerIsBuyer,
    counterpartyName: counterparty?.display_name || (viewerIsBuyer ? 'Agent' : 'Buyer'),
    counterpartyAvatarUrl: counterparty?.avatar_url || null,
    listingTitle: listing?.title || listing?.address || 'Property',
    unreadCount: viewerIsBuyer ? c.buyer_unread_count : c.agent_unread_count,
  };
}

// -----------------------------------------------------------------------------
// Shared reactive conversations cache (CHAT-17)
// -----------------------------------------------------------------------------
//
// The nav-level chat-unread badge MUST agree with the per-conversation row
// badges (ConversationList) at all times. The TECH-017 mistake was a separate,
// mount-only nav fetch that never re-ran. To avoid repeating it, the nav badge
// derives from THIS store — the same enriched data `fetchConversations`
// produces for the list — via `useSyncExternalStore`. Because every
// `fetchConversations` call publishes here, the post-read refetch
// (markConversationRead → onConversationsChanged → reloadConversations →
// fetchConversations) updates the nav count and the row badges together, with
// no remount.
//
// The cached items already carry a viewer-correct `unreadCount` (enrich picks
// buyer_unread_count vs agent_unread_count by role), so the unread-conversation
// count is simply the number of items with unreadCount > 0.

let conversationsCache: ConversationListItem[] | null = null;
let conversationsCacheUserId: string | null = null;
let inFlightLoad: Promise<void> | null = null;
const cacheListeners = new Set<() => void>();

function emitConversationsChange(): void {
  for (const listener of cacheListeners) listener();
}

function setConversationsCache(items: ConversationListItem[], userId: string): void {
  conversationsCache = items;
  conversationsCacheUserId = userId;
  emitConversationsChange();
}

/** Drop the cache (e.g. on sign-out) so a stale count never leaks across users. */
export function clearConversationsCache(): void {
  if (conversationsCache === null && conversationsCacheUserId === null) return;
  conversationsCache = null;
  conversationsCacheUserId = null;
  inFlightLoad = null;
  emitConversationsChange();
}

/**
 * Populate the cache for `userId` if it isn't already loaded for them. Safe to
 * call from multiple nav surfaces on mount: concurrent calls share one in-flight
 * fetch, and a cache already holding this user's data is a no-op. Updates from
 * an open thread still flow through `fetchConversations` independently.
 */
export async function ensureConversationsLoaded(userId: string): Promise<void> {
  if (conversationsCacheUserId === userId && conversationsCache !== null) return;
  if (inFlightLoad && conversationsCacheUserId === userId) return inFlightLoad;
  conversationsCacheUserId = userId; // claim the load so concurrent callers dedupe
  inFlightLoad = (async () => {
    try {
      await fetchConversations(userId);
    } finally {
      inFlightLoad = null;
    }
  })();
  return inFlightLoad;
}

function subscribeConversations(callback: () => void): () => void {
  cacheListeners.add(callback);
  return () => {
    cacheListeners.delete(callback);
  };
}

function getUnreadConversationCountSnapshot(): number {
  if (!conversationsCache) return 0;
  let count = 0;
  for (const item of conversationsCache) {
    if (item.unreadCount > 0) count += 1;
  }
  return count;
}

/**
 * Reactive count of conversations with unread messages for the current viewer
 * (NOT a sum of messages). Returns 0 until the cache is populated and during
 * SSR. Consumers should call `ensureConversationsLoaded` to seed it.
 */
export function useUnreadConversationCount(): number {
  return useSyncExternalStore(
    subscribeConversations,
    getUnreadConversationCountSnapshot,
    () => 0,
  );
}

/**
 * Stable empty snapshot. `useSyncExternalStore` compares snapshots by identity
 * and re-renders (then re-reads) on any change, so returning a fresh `[]` from
 * the getter would loop forever. `conversationsCache` is itself only replaced by
 * `setConversationsCache`, so it is a safe snapshot as-is.
 */
const EMPTY_CONVERSATIONS: ConversationListItem[] = [];

function getConversationsSnapshot(): ConversationListItem[] {
  return conversationsCache ?? EMPTY_CONVERSATIONS;
}

/**
 * BUG-060 — reactive read of the viewer's conversations from the same shared
 * cache that backs `useUnreadConversationCount` and the ConversationList rows.
 *
 * Added so a surface OUTSIDE /messages (the agent dashboard's Messages tile) can
 * report what /messages actually holds without issuing its own fetch — the
 * TECH-017 mistake this cache exists to prevent. Returns an empty array until
 * the cache is populated and during SSR; seed it with
 * `ensureConversationsLoaded`.
 */
export function useConversations(): ConversationListItem[] {
  return useSyncExternalStore(
    subscribeConversations,
    getConversationsSnapshot,
    () => EMPTY_CONVERSATIONS,
  );
}

/**
 * BUG-060 — has this viewer ever sent a message? Backs the agent dashboard's
 * "Respond to your first message" onboarding row.
 *
 * NOT derivable from the conversations cache. `conversations.first_response_at`
 * looks like the answer and is not: nothing writes it. No migration, Edge
 * Function or client sets it, no trigger on `messages`/`conversations` mentions
 * it, and in production all 12 conversations have it NULL (checked 2026-08-02) —
 * the CHAT-11 triggers maintain `last_message_at` and the unread counters only.
 * Reading it would leave the row permanently unticked no matter how many buyers
 * the agent replied to.
 *
 * So this is a one-row existence probe, not a message load. RLS does the
 * scoping: the participants SELECT policy already restricts `messages` to the
 * viewer's own conversations, so `sender_id=eq.<viewer>` returns exactly their
 * own sent messages. `select=id` keeps it off `raw_content` (BUG-032) and off
 * every other column.
 */
export async function hasSentAnyMessage(userId: string): Promise<boolean> {
  const token = await getValidAccessToken();
  if (!token) return false;

  const { data } = await apiGet<Array<{ id: string }>>(
    'messages',
    `sender_id=eq.${userId}&select=id&limit=1`,
    token
  );
  return !!data && data.length > 0;
}

/**
 * Fetch messages for a conversation in thread order (sent_at ASC). When
 * `sinceIso` is provided (polling), only messages strictly newer than that
 * timestamp are returned.
 */
export async function fetchMessages(conversationId: string, sinceIso?: string): Promise<DbMessage[]> {
  const token = await getValidAccessToken();
  if (!token) return [];

  let query = `conversation_id=eq.${conversationId}&select=${MESSAGE_SAFE_COLUMNS}&order=sent_at.asc`;
  if (sinceIso) {
    // Timestamps contain ':' and '+' — encode so '+' isn't read as a space.
    query += `&sent_at=gt.${encodeURIComponent(sinceIso)}`;
  }
  const { data } = await apiGet<DbMessage[]>('messages', query, token);
  return data || [];
}

// -----------------------------------------------------------------------------
// Mark-as-read
// -----------------------------------------------------------------------------

/**
 * Mark all of the counterparty's unread messages in a conversation as read.
 * Direct UPDATE of read_at only — allowed by the recipient RLS policy; the
 * BEFORE UPDATE guard rejects edits to any other column. CHAT-11 recomputes the
 * unread counter from this, so callers should refetch the conversation row
 * afterwards to refresh the list badge.
 *
 * Raw REST here (not the shared apiPatch helper) on purpose: apiPatch hardcodes
 * `Prefer: return=representation`, which makes PostgREST do a RETURNING * and so
 * require SELECT on every column — see the return=minimal note below.
 */
export async function markConversationRead(conversationId: string, userId: string): Promise<void> {
  const token = await getValidAccessToken();
  if (!token) return;
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;

  const query = `conversation_id=eq.${conversationId}&sender_id=neq.${userId}&read_at=is.null`;

  try {
    // return=minimal — authenticated lacks SELECT on raw_content/push_sent_at by
    // design (redaction); RETURNING * → 42501. We only write read_at and want no
    // row back, so ask PostgREST for a 204 with no representation.
    await fetch(`${SUPABASE_URL}/rest/v1/messages?${query}`, {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body: JSON.stringify({ read_at: new Date().toISOString() }),
    });
  } catch {
    // Fire-and-forget: a failed read-receipt must never break opening the thread
    // (the previous apiPatch path also never threw).
  }
}

// -----------------------------------------------------------------------------
// Send (Edge Function — never a direct messages INSERT)
// -----------------------------------------------------------------------------

/**
 * CHAT-19 — free-text phone-number detector for outgoing buyer messages.
 *
 * Used by the composer to decide whether to OFFER the buyer the share modal AFTER
 * a message is sent (the server still redacts the number regardless). This is a
 * deliberately separate helper from VALIDATION.PHONE_REGEX in config/constants.ts,
 * which is ANCHORED (^...$) and only validates a whole string; here we must SCAN
 * free text, so the patterns are un-anchored and mirror the server-side redaction
 * trigger (a Kenyan-format matcher plus a generic international matcher). Returns
 * true if either pattern matches anywhere in the text.
 */
const KE_PHONE_SCAN = /(\+?254|0)[\s-]?[17]\d{2}[\s-]?\d{3}[\s-]?\d{3}/;
const INTL_PHONE_SCAN = /\+\d[\d\s-]{6,14}\d/;

export function messageContainsPhone(text: string): boolean {
  return KE_PHONE_SCAN.test(text) || INTL_PHONE_SCAN.test(text);
}

/**
 * Send a message via the `send-message` Edge Function. Returns a typed result so
 * the composer can react to the paywall (402), rate-limit (429), too-long (400)
 * and participant (403/404) errors without parsing raw responses.
 */
export async function sendChatMessage(conversationId: string, content: string): Promise<SendMessageResult> {
  const token = await getValidAccessToken();
  if (!token) {
    return { ok: false, status: 401, message: 'You must be signed in to send messages.' };
  }
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    return { ok: false, status: 0, message: 'Messaging is unavailable right now.' };
  }

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ conversation_id: conversationId, content }),
    });

    const result = (await response.json().catch(() => ({}))) as {
      success?: boolean;
      id?: string;
      error?: string;
      message?: string;
      upgrade_url?: string;
      retry_after?: number;
    };

    if (response.ok && result.success) {
      return { ok: true, id: result.id };
    }

    return {
      ok: false,
      status: response.status,
      error: result.error,
      message: result.message,
      upgrade_url: result.upgrade_url,
      retry_after: result.retry_after,
    };
  } catch (err) {
    return { ok: false, status: 0, message: err instanceof Error ? err.message : 'Network error' };
  }
}
